import { FormData, LeadPayload, UpdateConsentPayload, TrackingData, ConsentState, ProductType, BonusSummaryData } from '../types';
import { getUTMParams, splitFullName, getAgentCodePartner, getAgentCodeInstaller } from './helpers';
import { getBonusPVForCap, getBonusHPForCap } from '../config/bonusLookup';

const API_BASE_URL = 'https://enpal-salesos-prd-we-function.azurewebsites.net/api';

/**
 * Verifica se un CAP è valido tramite API
 * @param zip - Codice postale da verificare
 * @returns true se il CAP è in lista, false altrimenti, null in caso di errore
 */
export async function checkZipCode(zip: string): Promise<boolean | null> {
  if (!zip || zip.length < 3) {
    return null;
  }

  const url = `${API_BASE_URL}/is-less-than-fifty-thousand?zip=${encodeURIComponent(zip)}`;

  try {
    const response = await fetch(url, {
      method: 'GET',
      mode: 'cors',
    });

    if (!response.ok) {
      return null;
    }

    const text = await response.text();

    // Prova a parsare come JSON
    try {
      const json = JSON.parse(text);
      if (typeof json === 'boolean') return json;
      if ('result' in json) return Boolean(json.result);
      if ('isLessThanFiftyThousand' in json) return Boolean(json.isLessThanFiftyThousand);
    } catch {
      // Prova come testo semplice
      const normalized = text.trim().toLowerCase();
      if (normalized === 'true') return true;
      if (normalized === 'false') return false;
    }

    return null;
  } catch (error) {
    console.error('Error checking ZIP code:', error);
    return null;
  }
}

/**
 * Costruisce i dati di tracking per il lead
 */
function buildTrackingData(): TrackingData {
  const utmParams = getUTMParams();

  return {
    client_id: `${Date.now()}-${Math.random().toString(36).substring(2)}`,
    conversion_url: window.location.href,
    entrance_url: sessionStorage.getItem('entrance_url') || window.location.href,
    url: window.location.href,
    useragent: navigator.userAgent,
    channel: utmParams.utm_source || 'organic',
    campaign: utmParams.utm_campaign || 'organic',
    ...utmParams,
  };
}

/**
 * Checks if a PV bonus object has any non-null bonus values
 */
function hasPVBonuses(bonus: ReturnType<typeof getBonusPVForCap>): boolean {
  return (
    bonus.ecobonus !== null ||
    bonus.pnrrCer !== null ||
    bonus.bonusRegionale !== null ||
    bonus.bonusProvinciale !== null ||
    bonus.risparmioBolletta !== null ||
    bonus.rivenditaEnergia !== null ||
    bonus.rivenditaSurplus !== null
  );
}

/**
 * Checks if an HP bonus object has any non-null bonus values
 */
function hasHPBonuses(bonus: ReturnType<typeof getBonusHPForCap>): boolean {
  return (
    bonus.contoTermico !== null ||
    bonus.ecobonus !== null ||
    bonus.bonusRegionale !== null ||
    bonus.bonusProvinciale !== null ||
    bonus.risparmioBolletta !== null
  );
}

/**
 * Sends lead to SOS system (fire-and-forget)
 * @param mongodbId - The MongoDB ID of the lead
 */
function sendLeadToSOS(mongodbId: string): void {
  try {
    fetch(`${API_BASE_URL}/send-lead-to-sos`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      mode: 'cors',
      body: JSON.stringify({ mongodbId }),
    });
  } catch (error) {
    console.error('Error sending lead to SOS:', error);
  }
}

function buildBonusSummaryData(zip: string, productType: ProductType): BonusSummaryData {
  const result: BonusSummaryData = {};

  switch (productType) {
    case 'Photovoltaic': {
      const pvBonus = getBonusPVForCap(zip);
      if (hasPVBonuses(pvBonus)) {
        result.pv = pvBonus;
      }
      break;
    }
    case 'Heatpump': {
      const hpBonus = getBonusHPForCap(zip);
      if (hasHPBonuses(hpBonus)) {
        result.hp = hpBonus;
      }
      break;
    }
    case 'Photovoltaic + Heatpump': {
      const pvBonus = getBonusPVForCap(zip);
      const hpBonus = getBonusHPForCap(zip);
      if (hasPVBonuses(pvBonus)) {
        result.pv = pvBonus;
      }
      if (hasHPBonuses(hpBonus)) {
        result.hp = hpBonus;
      }
      break;
    }
    default: {
      const pvBonus = getBonusPVForCap(zip);
      if (hasPVBonuses(pvBonus)) {
        result.pv = pvBonus;
      }
    }
  }

  return result;
}

/**
 * Invia i dati del lead all'API
 * @param formData - Dati del form
 * @param uid - UUID del lead
 * @param consent - Stato dei consensi
 * @returns L'ID MongoDB del lead se l'invio è andato a buon fine, null altrimenti
 */
export async function submitLead(
  formData: FormData,
  uid: string,
  consent: ConsentState
): Promise<string | null> {
  const timestamp = new Date().toISOString();
  const { firstName, lastName } = splitFullName(formData.name || '');
  const agentCodePartner = getAgentCodePartner();
  const agentCodeInstaller = getAgentCodeInstaller();

  // Normalizza il tipo prodotto per DB/bonus: PV_Retrofit -> Photovoltaic
  const productTypeForDb = formData.product_type === 'PV_Retrofit'
    ? 'Photovoltaic'
    : (formData.product_type || '');
  const productTypeForBonuses: ProductType =
    (formData.product_type === 'PV_Retrofit'
      ? 'Photovoltaic'
      : (formData.product_type as ProductType)) || 'Photovoltaic';

  const payload: LeadPayload = {
    created_at: timestamp,
    updated_at: timestamp,
    form_submitted_at: timestamp,
    tracking_data: buildTrackingData(),
    utm_device: getUTMParams().utm_device || '',
    utm_adposition: getUTMParams().utm_adposition || '',
    utm_placement: getUTMParams().utm_placement || '',
    country_code: 'it',
    uid,
    enpalMarketingConsent: consent.enpalMarketingConsent,
    thirdPartyMarketing: consent.thirdPartyMarketing,
    thirdPartyProfiling: consent.thirdPartyProfiling,
    'contact[first_name]': firstName,
    'contact[last_name]': lastName,
    'contact[email]': formData.email || '',
    'contact[phone]': (formData.phone || '').replace(/\s/g, ''),
    'contact[zipcode]': formData.zip || '',
    product_type: productTypeForDb,
    system_renovation: formData.product_type === 'PV_Retrofit' ? 1 : 0,
    pv_interest_since: formData.pv_interest_since || '',
    house_type: formData.house_type || '',
    heatpump_installation_space: formData.heatpump_installation_space || '',
    heating_source: formData.heating_source || '',
    house_squared_meters: formData.house_squared_meters || '',
    purchase_time: formData.purchase_time || '',
    bonus_summary_data: buildBonusSummaryData(formData.zip || '', productTypeForBonuses),
    agent_code_partner: agentCodePartner || '',
    agent_code_installer: agentCodeInstaller || '',
  };

  try {
    const response = await fetch(`${API_BASE_URL}/send-data-ita`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      mode: 'cors',
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error('Failed to submit data');
    }

    const data = await response.json();
    const id = data.id || null;

    // Fire-and-forget: send lead to SOS
    if (id) sendLeadToSOS(id);

    return id;
  } catch (error) {
    console.error('Error submitting lead:', error);
    return null;
  }
}

/**
 * Aggiorna i consensi del lead
 * @param uid - UUID del lead
 * @param consent - Nuovi consensi
 * @returns true se l'aggiornamento è andato a buon fine
 */
export async function updateConsent(
  uid: string,
  consent: Partial<ConsentState>
): Promise<boolean> {
  const payload: UpdateConsentPayload = {
    uid,
    enpalMarketingConsent: consent.enpalMarketingConsent || false,
    thirdPartyMarketing: consent.thirdPartyMarketing || false,
    thirdPartyProfiling: consent.thirdPartyProfiling || false,
  };

  try {
    const response = await fetch(`${API_BASE_URL}/update-data-ita`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      mode: 'cors',
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error('Failed to update consent');
    }

    return true;
  } catch (error) {
    console.error('Error updating consent:', error);
    return false;
  }
}
